/**
 * Client-side email API service
 * Uses Firebase Cloud Functions to send emails
 */

import { getFunctions, httpsCallable, Functions } from 'firebase/functions';
import { app } from '@/lib/firebase';

// Lazy initialization to avoid circular dependency
let functionsInstance: Functions | null = null;

function getFunctionsInstance(): Functions {
  if (!functionsInstance) {
    functionsInstance = getFunctions(app, 'us-central1');
  }
  return functionsInstance;
}

export interface PasswordResetResult {
  success: boolean;
  message?: string;
  code?: string;
}

export class EmailAPI {
  /**
   * Send welcome email to new users
   */
  static async sendWelcomeEmail(email: string, userName: string): Promise<boolean> {
    try {
      const functions = getFunctionsInstance();
      const sendEmail = httpsCallable(functions, 'sendWelcomeEmail');
      const result = await sendEmail({ email, userName });
      return (result.data as any).success || false;
    } catch (error) {
      console.error('Error sending welcome email:', error);
      return false;
    }
  }

  /**
   * Send password reset email
   */
  static async sendPasswordResetEmail(
    email: string,
    userName: string,
    resetLink: string
  ): Promise<boolean> {
    const result = await this.requestPasswordReset(email, userName, resetLink);
    return result.success;
  }

  static async requestPasswordReset(
    email: string,
    userName: string,
    resetLink: string
  ): Promise<PasswordResetResult> {
    try {
      const functions = getFunctionsInstance();
      const sendEmail = httpsCallable(functions, 'sendPasswordResetLinkEmail');
      const result = await sendEmail({ email, userName, resetLink });
      return {
        success: (result.data as any).success || false,
        message: (result.data as any).message,
      };
    } catch (error: any) {
      console.error('Error sending password reset email:', error);
      return {
        success: false,
        code: error?.code || 'unknown',
        message: error?.message || 'Failed to send password reset email',
      };
    }
  }

  /**
   * Send email verification
   */
  static async sendEmailVerification(
    email: string,
    userName: string,
    verificationLink: string
  ): Promise<boolean> {
    try {
      // Firebase Auth handles email verification automatically
      console.log('Email verification handled by Firebase Auth');
      return true;
    } catch (error) {
      console.error('Error sending email verification:', error);
      return false;
    }
  }

  /**
   * Send friend request notification
   */
  static async sendFriendRequestEmail(
    email: string,
    recipientName: string,
    senderName: string,
    senderProfileUrl: string
  ): Promise<boolean> {
    try {
      const functions = getFunctionsInstance();
      const sendEmail = httpsCallable(functions, 'sendFriendRequestEmail');
      const result = await sendEmail({ email, recipientName, senderName, senderProfileUrl });
      return (result.data as any).success || false;
    } catch (error) {
      console.error('Error sending friend request email:', error);
      return false;
    }
  }

  /**
   * Send new message notification
   */
  static async sendNewMessageEmail(
    email: string,
    recipientName: string,
    senderName: string,
    messagePreview: string
  ): Promise<boolean> {
    try {
      const functions = getFunctionsInstance();
      const sendEmail = httpsCallable(functions, 'sendNewMessageEmail');
      const result = await sendEmail({ email, recipientName, senderName, messagePreview });
      return (result.data as any).success || false;
    } catch (error) {
      console.error('Error sending new message email:', error);
      return false;
    }
  }

  /**
   * Send new comment notification
   */
  static async sendNewCommentEmail(
    email: string,
    recipientName: string,
    commenterName: string,
    commentText: string,
    postUrl: string
  ): Promise<boolean> {
    try {
      const functions = getFunctionsInstance();
      const sendEmail = httpsCallable(functions, 'sendNewCommentEmail');
      const result = await sendEmail({ email, recipientName, commenterName, commentText, postUrl });
      return (result.data as any).success || false;
    } catch (error) {
      console.error('Error sending new comment email:', error);
      return false;
    }
  }

  /**
   * Send mention notification
   */
  static async sendMentionEmail(
    email: string,
    recipientName: string,
    mentionerName: string,
    commentText: string,
    postUrl: string
  ): Promise<boolean> {
    try {
      const functions = getFunctionsInstance();
      const sendEmail = httpsCallable(functions, 'sendMentionEmail');
      const result = await sendEmail({ email, recipientName, mentionerName, commentText, postUrl });
      return (result.data as any).success || false;
    } catch (error) {
      console.error('Error sending mention email:', error);
      return false;
    }
  }

  /**
   * Send new reaction notification
   */
  static async sendNewReactionEmail(
    email: string,
    recipientName: string,
    reactorName: string,
    reactionType: string,
    postUrl: string
  ): Promise<boolean> {
    try {
      const functions = getFunctionsInstance();
      const sendEmail = httpsCallable(functions, 'sendNewReactionEmail');
      const result = await sendEmail({ email, recipientName, reactorName, reactionType, postUrl });
      return (result.data as any).success || false;
    } catch (error) {
      console.error('Error sending new reaction email:', error);
      return false;
    }
  }

  /**
   * Send system notification
   */
  static async sendSystemNotification(
    email: string,
    userName: string,
    title: string,
    message: string
  ): Promise<boolean> {
    try {
      const functions = getFunctionsInstance();
      const sendEmail = httpsCallable(functions, 'sendSystemNotification');
      const result = await sendEmail({ email, userName, title, message });
      return (result.data as any).success || false;
    } catch (error) {
      console.error('Error sending system notification:', error);
      return false;
    }
  }

  /**
   * Send group invite email
   */
  static async sendGroupInviteEmail(
    email: string,
    recipientName: string,
    inviterName: string,
    groupName: string,
    groupUrl: string
  ): Promise<boolean> {
    try {
      const functions = getFunctionsInstance();
      const sendEmail = httpsCallable(functions, 'sendGroupInviteEmail');
      const result = await sendEmail({ email, recipientName, inviterName, groupName, groupUrl });
      return (result.data as any).success || false;
    } catch (error) {
      console.error('Error sending group invite email:', error);
      return false;
    }
  }

  /**
   * Send page invite email
   */
  static async sendPageInviteEmail(
    email: string,
    recipientName: string,
    inviterName: string,
    pageName: string,
    pageUrl: string
  ): Promise<boolean> {
    try {
      const functions = getFunctionsInstance();
      const sendEmail = httpsCallable(functions, 'sendPageInviteEmail');
      const result = await sendEmail({ email, recipientName, inviterName, pageName, pageUrl });
      return (result.data as any).success || false;
    } catch (error) {
      console.error('Error sending page invite email:', error);
      return false;
    }
  }
}
